/**
 * Audit Log Filters Component
 *
 * Filter controls for the audit logs list.
 * @see US-19a.28
 */

import { Search, RotateCcw } from 'lucide-react';
import { Input, Select, DatePicker, Button } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import type { AuditAction } from '@bstock/shared';
import { useUsers } from '@/features/users';
import { ENTITY_TYPE_LABELS, ACTION_LABELS } from '../utils/format-audit-message';

interface AuditLogFiltersProps {
  entityType: string;
  action: string;
  actorId: string;
  searchQuery: string;
  dateRange: DateRange;
  onEntityTypeChange: (entityType: string) => void;
  onActionChange: (action: string) => void;
  onActorIdChange: (actorId: string) => void;
  onSearchChange: (query: string) => void;
  onDateRangeChange: (range: DateRange) => void;
  onReset: () => void;
}

const ACTIONS: AuditAction[] = ['CREATE', 'UPDATE', 'DELETE', 'VALIDATE', 'CANCEL', 'LOGIN', 'LOGOUT'];

const actionOptions = [
  { value: '', label: 'Toutes les actions' },
  ...ACTIONS.map((action) => ({
    value: action,
    label: ACTION_LABELS[action],
  })),
];

const entityTypeOptions = [
  { value: '', label: 'Tous les types' },
  ...Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => ({
    value,
    label: label.replace(/^(le |la |l')/, '').charAt(0).toUpperCase() +
           label.replace(/^(le |la |l')/, '').slice(1),
  })),
];

export function AuditLogFilters({
  entityType,
  action,
  actorId,
  searchQuery,
  dateRange,
  onEntityTypeChange,
  onActionChange,
  onActorIdChange,
  onSearchChange,
  onDateRangeChange,
  onReset,
}: AuditLogFiltersProps) {
  // Fetch users for the dropdown
  const { data: usersData } = useUsers({ page: 1, limit: 100 });

  const userOptions = [
    { value: '', label: 'Tous les utilisateurs' },
    ...(usersData?.items ?? []).map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName}`,
    })),
  ];

  const hasFilters = entityType || action || actorId || searchQuery || dateRange.start || dateRange.end;

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date range */}
      <DatePicker
        value={dateRange}
        onChange={onDateRangeChange}
      />

      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
        <Input
          type="text"
          placeholder="Rechercher (email, nom)..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* User filter */}
      <Select
        value={actorId}
        onChange={onActorIdChange}
        options={userOptions}
        allowEmpty={false}
        className="w-48"
      />

      {/* Entity type filter */}
      <Select
        value={entityType}
        onChange={onEntityTypeChange}
        options={entityTypeOptions}
        allowEmpty={false}
        className="w-44"
      />

      {/* Action filter */}
      <Select
        value={action}
        onChange={onActionChange}
        options={actionOptions}
        allowEmpty={false}
        className="w-40"
      />

      {/* Reset button */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          className="text-neutral-500 hover:text-neutral-700"
        >
          <RotateCcw className="w-4 h-4 mr-1" />
          Reinitialiser
        </Button>
      )}
    </div>
  );
}
